import { InjectionToken } from '@angular/core';

/**
 * Product number types supported for formatting.
 *
 * @example
 *
 * ```typescript
 * const attributeType: AccountNumberType = 'iban';
 * const format = service.getDefaultFormat(attributeType);
 * ```
 */
export type AccountNumberType = 'cardNumber' | 'iban' | 'bban';

/**
 * Default formatting specification with uniform 4-character segments.
 *
 * @example
 *
 * ```typescript
 * return this.accountsDisplayingFormat?.[attributeType] ?? DefaultAccountsDisplayingFormat;
 * ```
 */
export const DefaultAccountsDisplayingFormat: AccountsDisplayingFormat = {
  segments: 4,
};

/**
 * Interface defining account number formatting specifications.
 *
 * @example
 *
 * ```typescript
 * export const DefaultAccountsDisplayingFormat: AccountsDisplayingFormat = {
 *   segments: 4,
 * };
 * ```
 */
export interface AccountsDisplayingFormat {
  /**
   * The size (or array of sizes) the segments should have.
   */
  segments: number | Array<number>;
  /**
   * The separator to use between segments.
   */
  separator?: string;
}

/**
 * Definitions of the display formats of the account formatting configuration mapping account types
 *
 * @example
 *
 * ```typescript
 * constructor(
 *   @Optional()
 *   @Inject(ACCOUNTS_DISPLAYING_FORMAT)
 *   private readonly accountsDisplayingFormat: AccountsDisplayingFormatConfiguration,
 * ) {}
 * ```
 */
export type AccountsDisplayingFormatConfiguration = {
  /**
   * Account displaying formats for iban, bban values
   */
  [key in AccountNumberType]?: AccountsDisplayingFormat;
} & {
  /**
   * Account displaying format for card numbers
   */
  cardNumber?: AccountsDisplayingFormat & {
    cardProviders?: Record<string, AccountsDisplayingFormat>;
  };
};

/**
 * Injection token for providing custom account number formatting configuration.
 *
 * @example
 *
 * ```typescript
 * @Inject(ACCOUNTS_DISPLAYING_FORMAT)
 * private readonly accountsDisplayingFormat: AccountsDisplayingFormatConfiguration
 * ```
 */
export const ACCOUNTS_DISPLAYING_FORMAT = new InjectionToken<AccountsDisplayingFormatConfiguration>(
  'ACCOUNTS_DISPLAYING_FORMAT_TOKEN',
);
